import { FC } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import CodeIcon from '@mui/icons-material/Code';
import CampaignIcon from '@mui/icons-material/Campaign';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { projectTemplates, IProjectTemplate } from 'data/templates/projectTemplates';

type TTemplateSelectionProps = {
  onSelectTemplate: (template: IProjectTemplate) => void;
};

const getIcon = (iconName: string) => {
  const iconProps = { sx: { fontSize: 32, color: 'white' } };
  switch (iconName) {
    case 'person':
      return <PersonIcon {...iconProps} />;
    case 'code':
      return <CodeIcon {...iconProps} />;
    case 'campaign':
      return <CampaignIcon {...iconProps} />;
    case 'handshake':
      return <HandshakeIcon {...iconProps} />;
    default:
      return <AddIcon {...iconProps} />;
  }
};

const TemplateSelection: FC<TTemplateSelectionProps> = ({ onSelectTemplate }) => {
  return (
    <Box>
      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          mb: 3,
          textAlign: 'center',
        }}
      >
        Choose a template to get started quickly, or start from scratch.
      </Typography>

      <Grid container spacing={2}>
        {projectTemplates.map((template) => (
          <Grid item xs={12} sm={6} key={template.id}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: template.color,
                  boxShadow: `0 4px 12px ${template.color}20`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardActionArea
                onClick={() => onSelectTemplate(template)}
                sx={{
                  height: '100%',
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2.5,
                    backgroundColor: template.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  {getIcon(template.icon)}
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 0.5,
                  }}
                >
                  {template.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.5,
                  }}
                >
                  {template.description}
                </Typography>
                {template.boards.length > 0 && (
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {template.boards.map((board) => (
                      <Typography
                        key={board.name}
                        variant="caption"
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          backgroundColor: 'background.default',
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                        }}
                      >
                        {board.name}
                      </Typography>
                    ))}
                    {template.docs.length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          backgroundColor: 'background.default',
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                        }}
                      >
                        +{template.docs.length} docs
                      </Typography>
                    )}
                  </Box>
                )}
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default TemplateSelection;
